'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { API_ORIGIN } from '@/app/constants';
import { useFetchStream } from '@/app/hooks';
import { VOICE_STATUS_SSE_PATH } from '../constants';
import type { MaterialV1GetVoiceStatusReply, MaterialV1Voice } from '../types';

interface UseVoiceClonePendingSseParams {
  voiceId: string;
  enabled: boolean;
  onVoiceSucceeded: (voice: MaterialV1Voice) => void;
  onVoiceProcessing: (payload: { voiceId: string }) => void;
  onVoiceFailed: (payload: { voiceId: string }) => void;
}

/**
 * 订阅单个音色复刻任务状态，和任务历史卡片一样复用公共 SSE hook。
 */
export function useVoiceClonePendingSse({
  voiceId,
  enabled,
  onVoiceSucceeded,
  onVoiceProcessing,
  onVoiceFailed,
}: UseVoiceClonePendingSseParams): void {
  const t = useTranslations('tools.voiceClone.submit');
  const onVoiceSucceededRef = useRef(onVoiceSucceeded);
  onVoiceSucceededRef.current = onVoiceSucceeded;
  const onVoiceProcessingRef = useRef(onVoiceProcessing);
  onVoiceProcessingRef.current = onVoiceProcessing;
  const onVoiceFailedRef = useRef(onVoiceFailed);
  onVoiceFailedRef.current = onVoiceFailed;
  const shouldKeepSSERef = useRef(false);
  const voiceIdRef = useRef(voiceId);
  voiceIdRef.current = voiceId;

  const { run: getVoiceStatus, close: closeGetVoiceStatus } = useFetchStream<{ voice_id: string }>(
    VOICE_STATUS_SSE_PATH,
    {
      baseURL: API_ORIGIN,
      onMessage: (str) => {
        const dataObjects = str
          .split('data:')
          .map((d) => d.trim())
          .filter((d) => d);

        for (const dataString of dataObjects) {
          try {
            const data = JSON.parse(dataString) as MaterialV1GetVoiceStatusReply;
            const currentVoiceId = data.voice_id ?? voiceIdRef.current;

            if (data.status === 'success' && data.voice) {
              shouldKeepSSERef.current = false;
              onVoiceSucceededRef.current({
                ...data.voice,
                voice_id: data.voice.voice_id ?? currentVoiceId,
              });
              return;
            }

            if (data.status === 'failed' || data.status === 'unspecified') {
              shouldKeepSSERef.current = false;
              onVoiceFailedRef.current({ voiceId: currentVoiceId });
              toast({
                type: 'error',
                message: t('processingFailed'),
              });
              return;
            }

            if (data.status === 'processing') {
              onVoiceProcessingRef.current({ voiceId: currentVoiceId });
            }

            shouldKeepSSERef.current = true;
          } catch (error) {
            console.error('Error_from_voice_clone_status: ', error);
          }
        }
      },
      onClose: () => {
        if (shouldKeepSSERef.current) {
          getVoiceStatus({ voice_id: voiceIdRef.current });
        }
      },
    },
  );

  useEffect(() => {
    if (!enabled) {
      shouldKeepSSERef.current = false;
      closeGetVoiceStatus();
      return;
    }

    shouldKeepSSERef.current = true;
    getVoiceStatus({ voice_id: voiceId });

    return () => {
      shouldKeepSSERef.current = false;
      closeGetVoiceStatus();
    };
  }, [enabled, voiceId, getVoiceStatus, closeGetVoiceStatus]);
}
